import { Injectable } from '@angular/core';
import { ProjectMapFeatureResponse, ProjectMapManifestService } from './project-map-manifest.service';
import { boundsIntersect, candidateFields, geometryDistance, InspectionQuery, inspectionTolerance, MapFeatureCandidate, rankMapFeatureCandidates, validMapBounds } from '../helpers/project-map-inspection';

@Injectable({ providedIn: 'root' })
export class ProjectMapInspectionService {
  constructor(private api: ProjectMapManifestService) {}

  async inspect(query: InspectionQuery, signal: AbortSignal): Promise<{ candidates: MapFeatureCandidate[]; incomplete: boolean }> {
    signal.throwIfAborted();
    if (!validMapBounds(query.bbox) || query.bbox[2] - query.bbox[0] > 1 || query.bbox[3] - query.bbox[1] > 1) {
      throw new Error('Zoom in to inspect reference features.');
    }
    const layers = query.layers.filter(item => item.layer.inspect === 'full'
      && validMapBounds(item.layer.bounds) && boundsIntersect(item.layer.bounds, query.bbox))
      .sort((a, b) => b.drawOrder - a.drawOrder);
    const selected = layers.slice(0, 12);
    let incomplete = selected.length < layers.length;
    const responses: (ProjectMapFeatureResponse | null)[] = new Array(selected.length).fill(null);
    const controller = new AbortController();
    const cancel = () => controller.abort();
    signal.addEventListener('abort', cancel, { once: true });
    const deadline = setTimeout(cancel, 10000);
    let next = 0;
    try {
      await Promise.all(Array.from({ length: Math.min(3, selected.length) }, async () => {
        while (next < selected.length) {
          controller.signal.throwIfAborted();
          const index = next++; const { layer } = selected[index];
          try {
            responses[index] = await this.api.features(query.projectId, layer.id, query.catalogRevision, query.bbox, 50, controller.signal);
          } catch (error) {
            if (error?.status === 413) { incomplete = true; continue; }
            throw error;
          }
          controller.signal.throwIfAborted();
        }
      }));
      controller.signal.throwIfAborted();
      const candidates: MapFeatureCandidate[] = [];
      const budget = { remaining: 75000 };
      for (let index = 0; index < responses.length; index++) {
        const response = responses[index]; if (!response) continue;
        const { layer, drawOrder } = selected[index];
        if (response.catalogRevision !== query.catalogRevision || response.layerId !== layer.id
          || response.sourceVersionId !== layer.sourceVersionId || response.sublayerKey !== layer.sublayerKey
          || !Array.isArray(response.features) || response.features.length > 50
          || typeof response.truncated !== 'boolean' || JSON.stringify(response).length > 2 * 1024 * 1024) {
          throw new Error('Reference feature response did not match the displayed version.');
        }
        incomplete ||= response.truncated;
        const seen = new Set<string>();
        for (const feature of response.features) {
          if (typeof feature?.id !== 'string' || !feature.id.length || feature.id.length > 512 || seen.has(feature.id)) {
            throw new Error('Invalid reference feature identity.');
          }
          seen.add(feature.id);
          try {
            const distance = geometryDistance(feature.geometry, query.point, query.resolution, budget);
            if (distance <= inspectionTolerance(layer, (feature.geometry as { type?: string })?.type)) candidates.push({ layer, featureId: feature.id, drawOrder, distance,
              fields: candidateFields(layer, feature.properties), geometry: feature.geometry,
              properties: feature.properties || {},
              fieldNames: layer.style.popupFields.map(field => field.field) });
          } catch { incomplete = true; }
        }
      }
      signal.throwIfAborted();
      const ranked = rankMapFeatureCandidates(candidates, query.point);
      return { candidates: ranked.slice(0, 20), incomplete: incomplete || ranked.length > 20 };
    } finally {
      clearTimeout(deadline);
      signal.removeEventListener('abort', cancel);
      controller.abort();
    }
  }
}
